import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { feedbackApi } from "../../services/feedbackApi";
import type { FeedbackTicket } from "../../types";

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  submitted: { label: "Submitted", color: "#6b7280", bg: "#f3f4f6" },
  "under-review": { label: "Under Review", color: "#d97706", bg: "#fef3c7" },
  planned: { label: "Planned", color: "#2563eb", bg: "#eff6ff" },
  "in-progress": { label: "In Progress", color: "#7c3aed", bg: "#f5f3ff" },
  launched: { label: "Launched", color: "#16a34a", bg: "#dcfce7" },
  "no-plan": { label: "No Plan", color: "#6b7280", bg: "#f3f4f6" },
  duplicate: { label: "Duplicate", color: "#6b7280", bg: "#f3f4f6" },
};

export default function MyTickets() {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [tickets, setTickets] = useState<FeedbackTicket[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orgId) loadTickets();
  }, [orgId]);

  async function loadTickets() {
    try {
      const data = await feedbackApi.list(orgId!);
      setTickets(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <div className="empty-state">Loading tickets...</div>;

  if (tickets.length === 0) {
    return <div className="empty-state"><p>No submissions yet. Use the Submit tab to report a bug or request a feature.</p></div>;
  }

  return (
    <div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Title</th>
            <th>Priority</th>
            <th>Status</th>
            <th>Submitted</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((t) => {
            const s = STATUS_LABELS[t.status] || STATUS_LABELS.submitted;
            return (
              <tr key={t.ticketId}>
                <td>{t.type === "bug" ? "🐛" : "💡"}</td>
                <td>
                  <div style={{ fontWeight: 500 }}>{t.title}</div>
                  {t.adminNotes && (
                    <div style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: "0.25rem" }}>
                      📝 {t.adminNotes}
                    </div>
                  )}
                </td>
                <td style={{ textTransform: "capitalize" }}>{t.priority || "—"}</td>
                <td>
                  <span style={{ display: "inline-block", padding: "0.15rem 0.5rem", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 500, background: s.bg, color: s.color }}>
                    {s.label}
                  </span>
                </td>
                <td style={{ fontSize: "0.85rem", color: "#6b7280" }}>
                  {new Date(t.createdAt).toLocaleDateString()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
